import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Link, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { getCurrentLiturgicalSeason } from './utils/liturgicalCalendar';
import './App.css';

// ── Lazy-load every page — only the current route's JS is downloaded ──
const Home                       = lazy(() => import('./pages/Home'));
const Overview                   = lazy(() => import('./pages/Overview'));
const About                      = lazy(() => import('./pages/About'));
const History                    = lazy(() => import('./pages/History'));
const Leadership                 = lazy(() => import('./pages/Leadership'));
const Archdeaconries             = lazy(() => import('./pages/Archdeaconries'));
const ArchdeaconryDetail         = lazy(() => import('./pages/ArchdeaconryDetail'));
const News                       = lazy(() => import('./pages/News'));
const NewsDetail                 = lazy(() => import('./pages/NewsDetail'));
const Events                     = lazy(() => import('./pages/Events'));
const Contact                    = lazy(() => import('./pages/Contact'));
const NoticeBoard                = lazy(() => import('./pages/NoticeBoard'));
const Search                     = lazy(() => import('./pages/Search'));
const Login                      = lazy(() => import('./pages/Login'));
const Gallery                    = lazy(() => import('./pages/Gallery'));
const GalleryDetail              = lazy(() => import('./pages/GalleryDetail'));
const BookAppointment            = lazy(() => import('./pages/BookAppointment'));
const CancelAppointment          = lazy(() => import('./pages/CancelAppointment'));
const VerifyAppointment          = lazy(() => import('./pages/VerifyAppointment'));
const AdminDashboard             = lazy(() => import('./pages/AdminDashboard'));
const NewsManagement             = lazy(() => import('./pages/NewsManagement'));
const EventsManagement           = lazy(() => import('./pages/EventsManagement'));
const AnnouncementsManagement    = lazy(() => import('./pages/AnnouncementsManagement'));
const StructureManagement        = lazy(() => import('./pages/StructureManagement'));
const UserManagement             = lazy(() => import('./pages/UserManagement'));
const GalleryManagement          = lazy(() => import('./pages/GalleryManagement'));
const AuditLog                   = lazy(() => import('./pages/AuditLog'));
const DiocesanContentManagement  = lazy(() => import('./pages/DiocesanContentManagement'));
const AppointmentsManagement     = lazy(() => import('./pages/AppointmentsManagement'));
const PrivacyPolicy              = lazy(() => import('./pages/PrivacyPolicy'));
const NotFound                   = lazy(() => import('./pages/NotFound'));
const Footer                     = lazy(() => import('./components/Footer'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PageLoader() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
      <div style={{ width: 36, height: 36, border: '4px solid #e5e7eb', borderTop: '4px solid #4A1942', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Top-level links — Diocese sub-pages live under a dropdown
const DIOCESE_LINKS = [
  { to: '/overview',       label: 'Overview'     },
  { to: '/about',          label: 'About'        },
  { to: '/history',        label: 'History'      },
  { to: '/leadership',     label: 'Leadership'   },
  { to: '/archdeaconries', label: 'Structure'    },
];

const TOP_LINKS = [
  { to: '/',        label: 'Home'         },
  { to: '/news',    label: 'News'         },
  { to: '/events',  label: 'Events'       },
  { to: '/gallery', label: 'Gallery'      },
  { to: '/notices', label: 'Notice Board' },
  { to: '/book',    label: 'Book Appointment' },
  { to: '/contact', label: 'Contact'      },
];

// Diocese dropdown — pure CSS :hover, no JS state needed
function DiocesesDropdown() {
  return (
    <div className="navbar-dropdown-wrap">
      <button className="navbar-link navbar-dropdown-btn">
        Diocese <span className="navbar-dropdown-caret">▾</span>
      </button>
      {/* Always rendered — CSS controls visibility/opacity */}
      <div className="navbar-dropdown-menu" role="menu">
        {DIOCESE_LINKS.map(({ to, label }) => (
          <NavLink
            key={to}
            to={to}
            role="menuitem"
            className={({ isActive }) => `navbar-dropdown-item${isActive ? ' active' : ''}`}
          >
            {label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function Navbar({ season }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* ── Main bar ── */}
      <nav className="main-navbar" style={{ background: season.gradient, '--nav-text': season.textColor || '#fff' }}>
        <div className="navbar-inner">

          {/* icon-192.png is 36 KB — no need to load the 1.9 MB original in the navbar */}
          <Link to="/" className="navbar-logo-link">
            <img src="/icon-192.png" alt="ACK Meru Diocese" className="navbar-logo" />
          </Link>

          {/* Diocese name + liturgical season */}
          <div className="navbar-brand">
            <span className="navbar-title">ACK Meru Diocese</span>
            <span className="navbar-season">{season.name}</span>
          </div>

          {/* Desktop nav links */}
          <div className="navbar-links">
            <NavLink
              to="/"
              end
              className={({ isActive }) => `navbar-link${isActive ? ' active' : ''}`}
            >
              Home
            </NavLink>

            <DiocesesDropdown />

            {TOP_LINKS.slice(1).map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `navbar-link${isActive ? ' active' : ''}`}
              >
                {label}
              </NavLink>
            ))}

          </div>

          {/* Mobile hamburger */}
          <button
            className="navbar-hamburger"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen(o => !o)}
          >
            {open ? '✕' : '☰'}
          </button>
        </div>
      </nav>

      {/* ── Mobile slide-down menu ── */}
      {open && (
        <div className="navbar-mobile-menu" style={{ background: season.gradient, '--nav-text': season.textColor || '#fff' }}>
          <NavLink to="/" end className={({ isActive }) => `navbar-mobile-link${isActive ? ' active' : ''}`} onClick={() => setOpen(false)}>Home</NavLink>
          {/* Diocese sub-pages shown with indent */}
          <div className="navbar-mobile-section-label">Diocese</div>
          {DIOCESE_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `navbar-mobile-link indented${isActive ? ' active' : ''}`} onClick={() => setOpen(false)}>
              {label}
            </NavLink>
          ))}
          {TOP_LINKS.slice(1).map(({ to, label }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `navbar-mobile-link${isActive ? ' active' : ''}`} onClick={() => setOpen(false)}>
              {label}
            </NavLink>
          ))}
        </div>
      )}
    </>
  );
}

// Ping the Render backend the moment the app loads so it wakes up
// before the user reaches the login form (free tier cold start fix).
function useBackendWarmup() {
  useEffect(() => {
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
    fetch(`${base}/api/health`).catch(() => {});
  }, []);
}

function App() {
  const [season, setSeason] = useState(getCurrentLiturgicalSeason());
  useBackendWarmup();

  useEffect(() => {
    const timer = setInterval(() => setSeason(getCurrentLiturgicalSeason()), 60_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Navbar season={season} />

          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/"               element={<Home />} />
              <Route path="/overview"       element={<Overview />} />
              <Route path="/about"          element={<About />} />
              <Route path="/history"        element={<History />} />
              <Route path="/leadership"     element={<Leadership />} />
              <Route path="/archdeaconries" element={<Archdeaconries />} />
              <Route path="/archdeaconries/:id" element={<ArchdeaconryDetail />} />
              <Route path="/news"           element={<News />} />
              <Route path="/news/:id"       element={<NewsDetail />} />
              <Route path="/events"         element={<Events />} />
              <Route path="/gallery"        element={<Gallery />} />
              <Route path="/gallery/:id"    element={<GalleryDetail />} />
              <Route path="/notices"        element={<NoticeBoard />} />
              <Route path="/search"         element={<Search />} />
              <Route path="/contact"        element={<Contact />} />
              <Route path="/book"                         element={<BookAppointment />} />
              <Route path="/cancel-appointment/:token"   element={<CancelAppointment />} />
              <Route path="/verify/:bookingRef"          element={<VerifyAppointment />} />
              <Route path="/login"          element={<Login />} />

              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>}>
                <Route index element={
                  <div className="dashboard-home">
                    <h2>Dashboard Overview</h2>
                    <p>Welcome to the ACK Meru Diocese Admin Panel</p>
                  </div>
                } />
                <Route path="news" element={
                  <ProtectedRoute allowedRoles={['super_admin','management_admin']}><NewsManagement /></ProtectedRoute>
                } />
                <Route path="events" element={
                  <ProtectedRoute allowedRoles={['super_admin','management_admin']}><EventsManagement /></ProtectedRoute>
                } />
                <Route path="announcements" element={<AnnouncementsManagement />} />
                <Route path="structure" element={
                  <ProtectedRoute requiredRole="super_admin"><StructureManagement /></ProtectedRoute>
                } />
                <Route path="users" element={
                  <ProtectedRoute requiredRole="super_admin"><UserManagement /></ProtectedRoute>
                } />
                <Route path="gallery" element={
                  <ProtectedRoute allowedRoles={['super_admin','management_admin']}><GalleryManagement /></ProtectedRoute>
                } />
                <Route path="audit-log" element={
                  <ProtectedRoute requiredRole="super_admin"><AuditLog /></ProtectedRoute>
                } />
                <Route path="diocese-content" element={
                  <ProtectedRoute allowedRoles={['super_admin','management_admin']}><DiocesanContentManagement /></ProtectedRoute>
                } />
                <Route path="appointments" element={<AppointmentsManagement />} />
              </Route>

              <Route path="/privacy" element={<PrivacyPolicy />} />

              {/* 404 — must be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </ErrorBoundary>

          <Suspense fallback={null}>
            <Footer />
          </Suspense>

          {/* Floating WhatsApp button — shown only when env var is set */}
          {process.env.REACT_APP_WHATSAPP_NUMBER && (
            <a
              href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat on WhatsApp"
              style={{
                position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 9999,
                width: '54px', height: '54px', borderRadius: '50%',
                background: '#25D366', color: '#fff', textDecoration: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 18px rgba(37,211,102,0.45)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(37,211,102,0.6)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 18px rgba(37,211,102,0.45)';
              }}
            >
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
              </svg>
            </a>
          )}
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
